/*@jsxRuntime automatic @jsxImportSource react*/
/*Conflicting info: Used to have*/
/*GRANT OWNERSHIP ON SCHEMA hightouch_audit TO ROLE identifier($ht_default_role);*/
/*We don't really know which is correct; the below mirrors in-app instructions.*/
/*<!-- vale off -->*/
/*<!-- vale on -->*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1733875200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Dec 11 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "databricks",
        type: "source",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Databricks credential setup",
          "depth": 0,
          "id": "databricks-credential-setup"
        }, {
          "value": "Configuring a service principal with OAuth",
          "depth": 1,
          "id": "configuring-a-service-principal-with-oauth"
        }, {
          "value": "Alternative: Configuring a Personal Access Token (PAT)",
          "depth": 1,
          "id": "alternative-configuring-a-personal-access-token-pat"
        }, {
          "value": "Connection configuration",
          "depth": 0,
          "id": "connection-configuration"
        }, {
          "value": "Configure your source",
          "depth": 1,
          "id": "configure-your-source"
        }, {
          "value": "Choose your sync engine",
          "depth": 1,
          "id": "choose-your-sync-engine"
        }, {
          "value": "Provide credentials",
          "depth": 1,
          "id": "provide-credentials"
        }, {
          "value": "Test your connection",
          "depth": 0,
          "id": "test-your-connection"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Unity Catalog support",
          "depth": 1,
          "id": "unity-catalog-support"
        }, {
          "value": "Subquery error",
          "depth": 1,
          "id": "subquery-error"
        }, {
          "value": "Error connecting to the database. Unauthorized/Forbidden: 403",
          "depth": 1,
          "id": "error-connecting-to-the-database-unauthorizedforbidden-403"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      a: "a",
      h3: "h3",
      ol: "ol",
      li: "li",
      strong: "strong",
      code: "code",
      em: "em",
      ul: "ul",
      img: "img",
      h4: "h4",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components), {SyncEngineSnippet, EngineComparisonTable, SourceTestSnippet, SyncLogsSnippet, CommonErrorsPreface, PylonLink} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!EngineComparisonTable) _missingMdxReference("EngineComparisonTable", true);
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    if (!SourceTestSnippet) _missingMdxReference("SourceTestSnippet", true);
    if (!SyncEngineSnippet) _missingMdxReference("SyncEngineSnippet", true);
    if (!SyncLogsSnippet) _missingMdxReference("SyncLogsSnippet", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch lets you pull data stored in Databricks and push it to downstream destinations.\nHightouch connects to Databricks using ", _jsx(_components.a, {
          href: "https://en.wikipedia.org/wiki/Open_Database_Connectivity",
          children: "Open Database Connectivity"
        }), " (ODBC).\nThis guide walks you through getting your ODBC URL for your Databricks cluster and\nusing your credentials to connect to Hightouch."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["You can connect Databricks to Hightouch using\n", _jsx(_components.a, {
            href: "https://hightouch.com/blog/hightouch-databricks-partner-connect",
            children: "Databricks Partner Connect"
          }), "\nto bypass the setup steps outlined below. You can learn more about this in\n", _jsx(_components.a, {
            href: "https://docs.databricks.com/en/partner-connect/index.html",
            children: "Databricks' documentation"
          }), "."]
        })
      }), "\n", _jsx(WhitelistDetails, {
        target: "Databricks warehouse",
        privatelink: {
          anchor: "databricks",
          supportedIntegrations: ["aws-privatelink"]
        }
      }), "\n", _jsx(_components.h2, {
        id: "databricks-credential-setup",
        children: "Databricks credential setup"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch allows you to authenticate with Databricks using one of two methods:\nEither ", _jsx(_components.a, {
          href: "https://docs.databricks.com/en/dev-tools/auth/oauth-u2m.html",
          children: "OAuth"
        }), " or\na ", _jsx(_components.a, {
          href: "https://docs.databricks.com/en/dev-tools/auth/pat.html",
          children: "Personal Access Token (PAT)"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "We recommend using OAuth for configuring new connections."
      }), "\n", _jsx(_components.h3, {
        id: "configuring-a-service-principal-with-oauth",
        children: "Configuring a service principal with OAuth"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Create a ", _jsx(_components.a, {
              href: "https://docs.databricks.com/en/admin/users-groups/service-principals.html",
              children: "service principal"
            }), "."]
          }), "\n", _jsxs(_components.ol, {
            children: ["\n", _jsxs(_components.li, {
              children: ["\n", _jsxs(_components.p, {
                children: ["In your Databricks workspace, go to the user dropdown at the top right and select ", _jsx(_components.strong, {
                  children: "Settings"
                }), "."]
              }), "\n"]
            }), "\n", _jsxs(_components.li, {
              children: ["\n", _jsxs(_components.p, {
                children: ["In the ", _jsx(_components.strong, {
                  children: "Identity and access"
                }), " page, find ", _jsx(_components.strong, {
                  children: "Service Principals"
                }), " and click ", _jsx(_components.strong, {
                  children: "Manage"
                }), "."]
              }), "\n"]
            }), "\n", _jsxs(_components.li, {
              children: ["\n", _jsxs(_components.p, {
                children: ["Choose ", _jsx(_components.strong, {
                  children: "Add new"
                }), ", give your service principal a name, and click ", _jsx(_components.strong, {
                  children: "Add"
                }), "."]
              }), "\n"]
            }), "\n", _jsxs(_components.li, {
              children: ["\n", _jsxs(_components.p, {
                children: ["Take note of the Service Principal name as well as the ", _jsx(_components.strong, {
                  children: "Application id"
                }), " listed on the Service Principal\nConfigurations page. This is a UUID (for example, ", _jsx(_components.code, {
                  children: "63fc7e90-a8a2-4639-afd8-36ef6bb67cfa"
                }), "). You may\nneed this later to complete setup."]
              }), "\n"]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: "Create an OAuth secret pair."
          }), "\n", _jsxs(_components.ol, {
            children: ["\n", _jsxs(_components.li, {
              children: ["\n", _jsx(_components.p, {
                children: "On the page for the Service Principal created above, to go the Secrets tab."
              }), "\n"]
            }), "\n", _jsxs(_components.li, {
              children: ["\n", _jsxs(_components.p, {
                children: ["Click ", _jsx(_components.strong, {
                  children: "Generate secret"
                }), ". A dialog will pop up with a ", _jsx(_components.strong, {
                  children: "Secret"
                }), " and a ", _jsx(_components.strong, {
                  children: "Client ID"
                }), ".\nTake careful note of these values as it is only visible now; you can't look it up later."]
              }), "\n"]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Grant the necessary catalog permissions to your new Service Principal. The above steps are sufficient\nto connect and authenticate, but the Service Principal may not be ", _jsx(_components.em, {
              children: "authorized"
            }), " to execute\nqueries against your Databricks catalog. Depending on your Databricks workspace configuration,\nit may already be configured with the necessary permissions; but if the connection test fails,\nyou may need to follow these steps."]
          }), "\n", _jsxs(_components.ol, {
            children: ["\n", _jsxs(_components.li, {
              children: ["\n", _jsxs(_components.p, {
                children: ["In the Databricks sidebar on the left, click ", _jsx(_components.strong, {
                  children: "Catalog"
                }), " and locate your schema in the tree view."]
              }), "\n"]
            }), "\n", _jsxs(_components.li, {
              children: ["\n", _jsxs(_components.p, {
                children: ["Select the schema and open the ", _jsx(_components.strong, {
                  children: "Permissions"
                }), " page. Click the ", _jsx(_components.strong, {
                  children: "Grant"
                }), " button."]
              }), "\n"]
            }), "\n", _jsxs(_components.li, {
              children: ["\n", _jsxs(_components.p, {
                children: ["In the ", _jsx(_components.strong, {
                  children: "Principals"
                }), " input box, type the ", _jsx(_components.strong, {
                  children: "name"
                }), " of your new Service Principal."]
              }), "\n", _jsxs(_components.ul, {
                children: ["\n", _jsxs(_components.li, {
                  children: ["\n", _jsxs(_components.p, {
                    children: ["If you are using the Lightning sync engine (recommended; see below),\nselect the ", _jsx(_components.strong, {
                      children: "Data Editor"
                    }), " preset or manually grant the following permissions:"]
                  }), "\n", _jsxs(_components.ul, {
                    children: ["\n", _jsx(_components.li, {
                      children: _jsx(_components.code, {
                        children: "USE SCHEMA"
                      })
                    }), "\n", _jsx(_components.li, {
                      children: _jsx(_components.code, {
                        children: "EXECUTE"
                      })
                    }), "\n", _jsx(_components.li, {
                      children: _jsx(_components.code, {
                        children: "READ VOLUME"
                      })
                    }), "\n", _jsx(_components.li, {
                      children: _jsx(_components.code, {
                        children: "SELECT"
                      })
                    }), "\n", _jsx(_components.li, {
                      children: _jsx(_components.code, {
                        children: "MODIFY"
                      })
                    }), "\n", _jsx(_components.li, {
                      children: _jsx(_components.code, {
                        children: "CREATE TABLE"
                      })
                    }), "\n", _jsxs(_components.li, {
                      children: ["Make sure to enable the checkbox at the bottom to \"Also grant ", _jsx(_components.code, {
                        children: "USE CATALOG"
                      }), "\""]
                    }), "\n"]
                  }), "\n"]
                }), "\n", _jsxs(_components.li, {
                  children: ["\n", _jsxs(_components.p, {
                    children: ["If you are using the Basic sync engine,\nselect the ", _jsx(_components.strong, {
                      children: "Data Reader"
                    }), " preset or manually grant the following permissions:"]
                  }), "\n", _jsxs(_components.ul, {
                    children: ["\n", _jsx(_components.li, {
                      children: _jsx(_components.code, {
                        children: "USE SCHEMA"
                      })
                    }), "\n", _jsx(_components.li, {
                      children: _jsx(_components.code, {
                        children: "EXECUTE"
                      })
                    }), "\n", _jsx(_components.li, {
                      children: _jsx(_components.code, {
                        children: "READ VOLUME"
                      })
                    }), "\n", _jsx(_components.li, {
                      children: _jsx(_components.code, {
                        children: "SELECT"
                      })
                    }), "\n", _jsxs(_components.li, {
                      children: ["Make sure to enable the checkbox at the bottom to \"Also grant ", _jsx(_components.code, {
                        children: "USE CATALOG"
                      }), "\""]
                    }), "\n"]
                  }), "\n"]
                }), "\n"]
              }), "\n"]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["You may also need to configure ", _jsx(_components.em, {
              children: "warehouse"
            }), " permissions. As with the previous step\nconcerning ", _jsx(_components.em, {
              children: "catalog"
            }), " permissions, your Databricks workspace configuration may or may not\nautomatically grant the Service Principal permission to use the compute resources.\nYou can read more about it in the ", _jsx(_components.a, {
              href: "https://docs.databricks.com/en/compute/clusters-manage.html#compute-permissions",
              children: "Databricks docs"
            }), "."]
          }), "\n", _jsxs(_components.ol, {
            children: ["\n", _jsxs(_components.li, {
              children: ["\n", _jsxs(_components.p, {
                children: ["In the Databricks sidebar, click ", _jsx(_components.strong, {
                  children: "Compute"
                }), " and find your data warehouse. It may\nbe located on the ", _jsx(_components.strong, {
                  children: "SQL warehouses"
                }), " page."]
              }), "\n"]
            }), "\n", _jsxs(_components.li, {
              children: ["\n", _jsxs(_components.p, {
                children: ["On the far right column of the row corresponding to the desired warehouse, click\nthe icon for the kebab menu ", _jsx(_components.strong, {
                  children: "⋮"
                }), ", then ", _jsx(_components.strong, {
                  children: "Permissions"
                }), "."]
              }), "\n", _jsx(Info, {
                children: _jsxs(_components.p, {
                  children: ["If you do not see the ", _jsx(_components.strong, {
                    children: "Permissions"
                  }), " option, you may lack the privileges\nnecessary to configure permissions—even if you are a Databricks workspace\nadmin. If this happens, please contact your Databricks account admin."]
                })
              }), "\n"]
            }), "\n", _jsxs(_components.li, {
              children: ["\n", _jsxs(_components.p, {
                children: ["Enter the name of your Service Principal and ensure that the setting in the\ndropdown is set to ", _jsx(_components.strong, {
                  children: "Can use"
                }), ". Click ", _jsx(_components.strong, {
                  children: "Add"
                }), "."]
              }), "\n"]
            }), "\n"]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "alternative-configuring-a-personal-access-token-pat",
        children: "Alternative: Configuring a Personal Access Token (PAT)"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["In your Databricks Account console, go the ", _jsxs(_components.a, {
              href: "https://accounts.cloud.databricks.com/workspaces",
              children: [_jsx(_components.strong, {
                children: "Workspaces"
              }), " page"]
            }), ".\nSelect the relevant workspace and then click ", _jsx(_components.strong, {
              children: "Open workspace"
            }), "."]
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "sources/source-databricks-workspace.png",
              alt: "Databricks workspace settings"
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["In your workspace, go the ", _jsx(_components.strong, {
              children: "Compute"
            }), " page and click the relevant cluster. This brings you to its ", _jsx(_components.strong, {
              children: "Configuration"
            }), " tab."]
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "sources/source-databricks-settings.png",
              alt: "Databricks cluster settings"
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["At the bottom of the page, expand the ", _jsx(_components.strong, {
              children: "Advanced Options"
            }), " toggle and open the ", _jsx(_components.strong, {
              children: "JDBC/ODBC"
            }), " tab."]
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "sources/source-databricks-advanced.png",
              alt: "Databricks Advanced Options"
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["This tab displays your cluster's ", _jsx(_components.strong, {
              children: "Server Hostname"
            }), ", ", _jsx(_components.strong, {
              children: "Port"
            }), ", and ", _jsx(_components.strong, {
              children: "HTTP Path"
            }), ", which you need to connect to Hightouch.\nKeep the tab open, or save these to a secure location."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Create a ", _jsx(_components.strong, {
              children: "Personal access token"
            }), " by following ", _jsx(_components.a, {
              href: "https://docs.databricks.com/dev-tools/auth.html#pat",
              children: "the Databricks documentation"
            }), "."]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Once you've saved these Databricks credentials, you're ready to set up the connection in Hightouch."
      }), "\n", _jsx(_components.h2, {
        id: "connection-configuration",
        children: "Connection configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["To get started, go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/sources",
          children: [_jsx(_components.strong, {
            children: "Sources"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add source"
        }), " button.\nSelect ", _jsx(_components.strong, {
          children: "Databricks"
        }), " and follow the steps below."]
      }), "\n", _jsx(_components.h3, {
        id: "configure-your-source",
        children: "Configure your source"
      }), "\n", _jsx(_components.p, {
        children: "Enter the following required fields into Hightouch:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Server hostname"
          }), ": To find your server hostname, visit the Databricks web console and locate your cluster.\nThen, click to reveal Advanced options and navigate to the JDBC/ODBC tab."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Port"
          }), ": The default port number is 443, but yours may be different.\nTo find your port, visit the Databricks web console and locate your cluster.\nThen, click to reveal Advanced options and navigate to the JDBC/ODBC tab."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "HTTP path"
          }), ": To find your HTTP path, visit the Databricks web console and locate your cluster.\nThen, click to reveal Advanced options and navigate to the JDBC/ODBC tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["(Optional) ", _jsx(_components.strong, {
            children: "Catalog"
          }), ": You can optionally include a Databricks catalog.\nCatalogs are the first level of ", _jsx(_components.a, {
            href: "https://docs.databricks.com/en/data-governance/unity-catalog/index.html",
            children: "Unity Catalog"
          }), "'s three-level namespace.\nIf Lightning engine is enabled, the 'hightouch_audit' and 'hightouch_planner' ", _jsx(_components.a, {
            href: "https://hightouch.com/docs/syncs/lightning-sync-engine#warehouse-schemas",
            children: "schemas"
          }), "\nneed to be created in the catalog specified here. If this field is left empty,\nthese schemas should be created in the ", _jsx(_components.a, {
            href: "https://docs.databricks.com/en/data-governance/unity-catalog/create-catalogs.html#manage-the-default-catalog",
            children: "default catalog"
          }), ".\nNote that data in other catalogs can still be accessed for model creation by specifying\nthe full name (catalog.schema.table) in the Hightouch SQL interface."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Schema"
          }), ": The initial schema to use for the connection."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "SQL dialect"
          }), ": By default, Hightouch assumes that your queries use the Databricks SQL dialect.\nYou may wish to override this behavior if your queries use legacy ANSI SQL-92 syntax.\nSome features are not available when legacy syntax is used."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "choose-your-sync-engine",
        children: "Choose your sync engine"
      }), "\n", _jsx(SyncEngineSnippet, {
        source: "Databricks"
      }), "\n", _jsx(_components.h4, {
        id: "basic-versus-lightning-engine-comparison",
        children: "Basic versus Lightning engine comparison"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Lightning sync engine"
        }), " requires granting write access to your data warehouse,\nwhich makes its setup more involved than the Basic sync engine.\nHowever, it is more performant and reliable than the Basic engine.\nThis makes it the ideal choice to guarantee faster syncs, especially with large data models.\nIt also supports more features, such as\n", _jsx(_components.a, {
          href: "/syncs/warehouse-sync-logs",
          children: "Warehouse Sync Logs"
        }), ",\n", _jsx(_components.a, {
          href: "/match-booster/overview",
          children: "Match Booster"
        }), ",\nand ", _jsx(_components.a, {
          href: "https://hightouch.com/platform/identity-resolution",
          children: "Identity Resolution"
        }), "."]
      }), "\n", _jsx(EngineComparisonTable, {
        source: "Databricks"
      }), "\n", _jsx(_components.h4, {
        id: "lightning-engine-setup",
        children: "Lightning engine setup"
      }), "\n", _jsx(_components.p, {
        children: "To set up the Lightning engine, ensure that your Databricks user or service principal has the appropriate permissions.\nYou can do so by running the following SQL snippet."
      }), "\n", _jsxs(_components.p, {
        children: ["Before running the snippet, make sure to include your own username, which can be found in the top right corner\nof the Databricks web console. Alternatively, you can create an API-only service principal by following\n", _jsx(_components.a, {
          href: "https://docs.databricks.com/administration-guide/users-groups/service-principals.html",
          children: "these instructions"
        }), "."]
      }), "\n", "\n", "\n", "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "CREATE"
          }), " SCHEMA IF ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NOT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "EXISTS"
          }), " hightouch_audit;\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "CREATE"
          }), " SCHEMA IF ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NOT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "EXISTS"
          }), " hightouch_planner;\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ALL"
          }), " PRIVILEGES ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " SCHEMA hightouch_audit ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "<"
          }), "YOUR_USER", _jsx(_components.span, {
            className: "hljs-operator",
            children: ">"
          }), ";\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ALL"
          }), " PRIVILEGES ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " SCHEMA hightouch_planner ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "<"
          }), "YOUR_USER", _jsx(_components.span, {
            className: "hljs-operator",
            children: ">"
          }), ";\n"]
        })
      }), "\n", _jsxs(Info, {
        children: [_jsxs(_components.p, {
          children: ["If you are using a Service Principal to connect, you may need to specify the user\nabove by UUID rather than name. For example, if you have created a Service Principal\nnamed ", _jsx(_components.code, {
            children: "test_principal"
          }), " with an ", _jsx(_components.strong, {
            children: "Application Id"
          }), " of ", _jsx(_components.code, {
            children: "63fc7e90-a8a2-4639-afd8-36ef6bb67cfa"
          }), ",\nyou may need to run"]
        }), _jsx(_components.pre, {
          children: _jsxs(_components.code, {
            className: "hljs language-sql",
            children: [_jsx(_components.span, {
              className: "hljs-keyword",
              children: "GRANT"
            }), " ", _jsx(_components.span, {
              className: "hljs-keyword",
              children: "ALL"
            }), " PRIVILEGES ", _jsx(_components.span, {
              className: "hljs-keyword",
              children: "ON"
            }), " SCHEMA hightouch_audit ", _jsx(_components.span, {
              className: "hljs-keyword",
              children: "TO"
            }), " ", _jsx(_components.span, {
              className: "hljs-number",
              children: "63"
            }), "fc7e90", _jsx(_components.span, {
              className: "hljs-operator",
              children: "-"
            }), "a8a2", _jsx(_components.span, {
              className: "hljs-number",
              children: "-4639"
            }), _jsx(_components.span, {
              className: "hljs-operator",
              children: "-"
            }), "afd8", _jsx(_components.span, {
              className: "hljs-number",
              children: "-36"
            }), "ef6bb67cfa;\n"]
          })
        })]
      }), "\n", _jsx(_components.h3, {
        id: "provide-credentials",
        children: "Provide credentials"
      }), "\n", _jsxs(_components.p, {
        children: ["Enter the ", _jsx(_components.strong, {
          children: "Access token"
        }), " you generated in the ", _jsx(_components.a, {
          href: "#databricks-credential-setup",
          children: "Databricks credential setup"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "test-your-connection",
        children: "Test your connection"
      }), "\n", _jsx(SourceTestSnippet, {
        source: "Databricks"
      }), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.p, {
        children: ["Once your source configuration has passed the necessary validation, your source setup is complete. Next, you can set up\n", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "models"
        }), " to define which data you want to pull from Databricks."]
      }), "\n", _jsx(_components.p, {
        children: "The Databricks source supports these modeling methods:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["writing a query in ", _jsx(_components.a, {
            href: "/models/sql-editor",
            children: "the SQL editor"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["using the visual ", _jsx(_components.a, {
            href: "/models/table-selector",
            children: "table selector"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["leveraging existing ", _jsx(_components.a, {
            href: "/extensions/dbt-models",
            children: "dbt models"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["leveraging existing ", _jsx(_components.a, {
            href: "/extensions/sigma",
            children: "Sigma workbooks"
          })]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.a, {
          href: "/models/sql-editor",
          children: "SQL editor"
        }), " allows you to query data from all databases that your Databricks credentials\n", _jsx(_components.a, {
          href: "#databricks-credential-setup",
          children: "have access to"
        }), ", unless you ", _jsx(_components.a, {
          href: "#configure-your-source",
          children: "specify a catalog"
        }), ".\nThe ", _jsx(_components.a, {
          href: "/models/table-selector",
          children: "table selector"
        }), " only supports querying from the schema specified in\nthe ", _jsx(_components.a, {
          href: "#configure-your-source",
          children: "source configuration"
        }), "."]
      }), "\n", _jsx(SyncLogsSnippet, {
        source: "Databricks"
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h3, {
        id: "unity-catalog-support",
        children: "Unity Catalog support"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch integrates with Databricks' ", _jsx(_components.a, {
          href: "https://docs.databricks.com/en/data-governance/unity-catalog/index.html",
          children: "Unity Catalog"
        }), " feature.\nDuring ", _jsx(_components.a, {
          href: "#configure-your-source",
          children: "initial configuration"
        }), ", just make sure to include a ", _jsx(_components.strong, {
          children: "catalog"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "subquery-error",
        children: "Subquery error"
      }), "\n", _jsxs(_components.p, {
        children: ["Some Databricks ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "models"
        }), " return the following error:"]
      }), "\n", "\n", _jsx(_components.p, {
        children: _jsx(_components.code, {
          children: "java.lang.IllegalArgumentException: requirement failed: Subquery subquery#485, [id=#937] has not finished {...}."
        })
      }), "\n", "\n", _jsxs(_components.p, {
        children: ["This error can occur when the model query contains a subquery, especially when the subquery has an ", _jsx(_components.code, {
          children: "ORDER BY"
        }), " clause.\nFor example:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), " user_query.", _jsx(_components.span, {
            className: "hljs-operator",
            children: "*"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "FROM"
          }), " (\n   ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "*"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "FROM"
          }), " default.subscriptions_table\n   ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ORDER"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "BY"
          }), " last_name\n) user_query.", _jsx(_components.span, {
            className: "hljs-operator",
            children: "*"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The best way to resolve the error is to rewrite your model query to remove subqueries.\n", _jsx(_components.a, {
          href: "https://docs.databricks.com/sql/language-manual/sql-ref-syntax-qry-select-cte.html",
          children: "Common table expressions (CTE)"
        }), " are supported.\nIf you require assistance, don't hesitate to ", _jsx(PylonLink, {
          pylonText: "Hello, I'm running into issues with my Databrick models",
          linkText: "get in touch"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "error-connecting-to-the-database-unauthorizedforbidden-403",
        children: "Error connecting to the database. Unauthorized/Forbidden: 403"
      }), "\n", _jsxs(_components.p, {
        children: ["This error can occur when ", _jsx(_components.a, {
          href: "#test-your-connection",
          children: "testing the source connection"
        }), "\nor when running a sync that uses a Databricks model.\nIt is typically caused by an expired Databricks access token.\nTo solve this, ", _jsx(_components.a, {
          href: "https://docs.databricks.com/dev-tools/auth.html#pat",
          children: "generate a new token"
        }), "\nand insert it in the source configuration."]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
